/**
 * PPF booking as a step accordion (Car -> Package -> Brand -> Studio & Slot -> Your Details),
 * the same pattern as the product page: one step open at a time; finishing a step
 * folds it into a one-line summary and opens the next incomplete one; a step stays locked
 * until every step before it is done. Car/package/brand complete as soon as they're chosen;
 * studio & slot and details have several fields, so they complete on their Continue button.
 * PPF is only fitted at the studio, so pick & drop is the one service (no home installation). A live
 * summary sidebar holds the pay button. Paying performs a standalone Buy Now (clear cart, add
 * only the matched ppf_package variant, redirect to /checkout) so it can never mix with an
 * accessories cart - see assumption #5 in README.
 *
 * Note on pricing: the sidebar's "Total Amount" always shows the resolved
 * ppf_package variant's real price - the exact amount /cart/add.js will charge.
 * No separate tax/GST line is fabricated here, since Shopify's actual checkout tax
 * calculation (or included-tax pricing) is the source of truth and a hand-computed
 * number could silently mismatch what the customer is actually charged.
 */
(function () {
  var STEPS = ['car', 'package', 'brand', 'service', 'details'];
  var DATE_CHIP_COUNT = 14;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var ADVANCE_DELAY = reduceMotion ? 0 : 260;

  function readJson(selector) {
    var node = document.querySelector(selector);
    if (!node) return [];
    try { return JSON.parse(node.textContent); } catch (e) { return []; }
  }

  function formatMoney(amount) {
    return '₹' + (Number(amount) || 0).toLocaleString('en-IN');
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }
  function isoDate(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
  function friendlyDate(iso) {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  }

  document.addEventListener('DOMContentLoaded', function () {
    var root = document.getElementById('PpfBooking');
    if (!root) return;

    var vehicles = readJson('[data-vehicle-data]');
    var types = readJson('[data-ppf-types]');
    var brands = readJson('[data-ppf-brands]');
    var packages = readJson('[data-ppf-packages]');

    // --- Advance payment ---------------------------------------------------
    // When enabled AND the advance helper product exists, checkout charges only a
    // deposit: the helper variant (one "unit", e.g. Rs.100) is added N times so the
    // line total equals the deposit. Without that product we charge the full package.
    var advanceCfg = readJson('[data-ppf-advance-config]') || {};
    var advanceUnit = Number(advanceCfg.unit_price) || 0;
    var advanceVariantId = parseInt(advanceCfg.variant_id, 10) || 0;
    var advancePercent = Number(advanceCfg.percent) || 10;
    var advanceActive = !!advanceCfg.enabled && advanceVariantId > 0 && advanceUnit > 0;

    // Deposit is rounded to a whole number of units, so the charged amount always
    // matches the line total Shopify will compute (unit price x quantity).
    function advanceFor(packagePrice) {
      var price = Number(packagePrice) || 0;
      if (!advanceActive || !price) return null;
      var qty = Math.max(1, Math.round((price * advancePercent / 100) / advanceUnit));
      var amount = qty * advanceUnit;
      if (amount > price) { amount = price; qty = Math.max(1, Math.round(price / advanceUnit)); }
      return { quantity: qty, amount: amount, balance: Math.max(0, price - amount) };
    }
    var studios = readJson('[data-ppf-studios]');

    var accordion = root.querySelector('[data-ppf-acc]');
    var copy = accordion ? accordion.dataset : {};

    var state = {};
    var SERVICE_LABEL = 'Pick & Drop Service';
    // Service and details complete only once their Continue button has checked them.
    var confirmed = { service: false, details: false };

    var helpers = window.CarzeloCarSelector || {
      uniqueSorted: function (v) { return Array.from(new Set(v.filter(Boolean))).sort(); },
      yearsInRange: function (from, to) {
        var years = []; from = parseInt(from, 10); to = parseInt(to, 10);
        if (!from || !to || to < from) return years;
        for (var y = to; y >= from; y--) years.push(y);
        return years;
      }
    };

    function fillSelect(select, values) {
      var placeholder = select.options[0] ? select.options[0].textContent : 'Select';
      select.innerHTML = '';
      var opt0 = document.createElement('option');
      opt0.value = ''; opt0.textContent = placeholder;
      select.appendChild(opt0);
      values.forEach(function (v) {
        var opt = document.createElement('option');
        opt.value = v; opt.textContent = v;
        select.appendChild(opt);
      });
    }

    function clearOptions(stepName) {
      var container = root.querySelector('[data-ppf-options="' + stepName + '"]');
      if (container) container.innerHTML = '';
    }

    // A car change invalidates any protection type / brand / package already picked for
    // the *previous* car (different category = different price + variant) - without this,
    // the summary and checkout could keep charging for a package that doesn't match the
    // currently-selected car.
    function resetCarDependentState() {
      state.category = null;
      state.carLabel = null;
      state.typeId = null; state.typeName = null;
      state.brandId = null; state.brandName = null;
      state.packageId = null; state.packageName = null; state.packagePrice = null; state.variantId = null;
      clearOptions('protection_type');
      clearOptions('brand');
      var noPackages = root.querySelector('[data-ppf-no-packages]');
      if (noPackages) noPackages.hidden = true;
    }

    // --- Step 1: car ---
    var brandSelect = root.querySelector('[data-ppf-car-brand]');
    var modelSelect = root.querySelector('[data-ppf-car-model]');
    var yearSelect = root.querySelector('[data-ppf-car-year]');
    var carNote = root.querySelector('[data-ppf-car-note]');

    fillSelect(brandSelect, helpers.uniqueSorted(vehicles.map(function (v) { return v.brand; })));

    brandSelect.addEventListener('change', function () {
      var models = helpers.uniqueSorted(vehicles.filter(function (v) { return v.brand === brandSelect.value; }).map(function (v) { return v.model; }));
      fillSelect(modelSelect, models);
      modelSelect.disabled = !brandSelect.value;
      yearSelect.disabled = true;
      fillSelect(yearSelect, []);
      resetCarDependentState();
      if (carNote) carNote.hidden = true;
      refresh();
    });

    modelSelect.addEventListener('change', function () {
      var matches = vehicles.filter(function (v) { return v.brand === brandSelect.value && v.model === modelSelect.value; });
      var yearSet = new Set();
      matches.forEach(function (v) { helpers.yearsInRange(v.year_from, v.year_to).forEach(function (y) { yearSet.add(y); }); });
      var years = Array.from(yearSet).sort(function (a, b) { return b - a; });
      fillSelect(yearSelect, years);
      yearSelect.disabled = years.length === 0;
      resetCarDependentState();
      if (carNote) carNote.hidden = true;
      refresh();
    });

    yearSelect.addEventListener('change', function () {
      resetCarDependentState();
      var matched = vehicles.find(function (v) {
        return v.brand === brandSelect.value && v.model === modelSelect.value &&
          parseInt(yearSelect.value, 10) >= v.year_from && parseInt(yearSelect.value, 10) <= v.year_to;
      });
      state.category = matched ? matched.category : null;
      state.carLabel = matched ? (brandSelect.value + ' ' + modelSelect.value + ' (' + yearSelect.value + ')') : null;
      var typeCount = state.category ? renderProtectionTypes() : 0;
      // A car with no online package is a dead end - say so here instead of opening an empty step.
      if (carNote) carNote.hidden = !yearSelect.value || typeCount > 0;
      if (!typeCount) state.category = null;
      refresh();
      if (typeCount) advanceFrom('car');
    });

    // --- Options renderer shared by protection_type / brand / studio ---
    function renderOptions(stepName, items, labelFn, priceFn, onSelect) {
      var container = root.querySelector('[data-ppf-options="' + stepName + '"]');
      container.innerHTML = '';
      items.forEach(function (item) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ppf-booking__option';
        btn.dataset.id = item.id;
        btn.innerHTML = '<span class="ppf-booking__option-check">' +
          '<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="3"><path d="m5 12 5 5 9-9"/></svg></span>' +
          '<strong>' + labelFn(item) + '</strong>' + (priceFn ? '<span class="ppf-booking__option-meta">' + priceFn(item) + '</span>' : '');
        btn.addEventListener('click', function () {
          container.querySelectorAll('.ppf-booking__option').forEach(function (b) { b.classList.remove('is-selected'); });
          btn.classList.add('is-selected');
          onSelect(item);
        });
        container.appendChild(btn);
      });
      return container;
    }

    // --- Step 2: protection type (package), priced from the cheapest matching brand ---
    function renderProtectionTypes() {
      var available = types.map(function (t) {
        var matches = packages.filter(function (p) { return p.type_id === t.id && p.category === state.category; });
        if (!matches.length) return null;
        var minPrice = Math.min.apply(null, matches.map(function (p) { return Number(p.price); }));
        return { id: t.id, name: t.name, minPrice: minPrice };
      }).filter(Boolean);

      renderOptions('protection_type', available, function (t) { return t.name; }, function (t) {
        return 'Starting from ' + formatMoney(t.minPrice);
      }, function (t) {
        state.typeId = t.id; state.typeName = t.name;
        state.brandId = null; state.brandName = null;
        state.packageId = null; state.packageName = null; state.packagePrice = null; state.variantId = null;
        renderBrands();
        refresh();
        advanceFrom('package');
      });
      return available.length;
    }

    // --- Step 3: brand, priced with the real matched package for type+brand+category ---
    function renderBrands() {
      var available = brands.map(function (b) {
        var match = packages.find(function (p) { return p.type_id === state.typeId && p.brand_id === b.id && p.category === state.category; });
        return match ? { id: b.id, name: b.name, package: match } : null;
      }).filter(Boolean);

      root.querySelector('[data-ppf-no-packages]').hidden = available.length > 0;

      renderOptions('brand', available, function (b) { return b.name; }, function (b) {
        return formatMoney(b.package.price);
      }, function (b) {
        state.brandId = b.id; state.brandName = b.name;
        state.packageId = b.package.id;
        state.packageName = b.package.name;
        state.packagePrice = b.package.price;
        state.variantId = b.package.variant_id;
        refresh();
        advanceFrom('brand');
      });
    }

    // --- Step 4: studio & slot (pick & drop is the only PPF service) ---
    var pincodeCheck = root.querySelector('[data-ppf-pincode-check]');
    pincodeCheck.addEventListener('input', function () {
      pincodeCheck.value = pincodeCheck.value.replace(/\D/g, '');
      renderStudiosForPincode();
    });

    // Pincode lists (the studio's own list and the theme's Delhi NCR list) hold prefixes:
    // "110" covers every 110xxx code, "110044" only that one.
    function matchesPrefix(pincode, list) {
      return (list || []).some(function (p) {
        p = String(p).trim();
        return p && pincode.indexOf(p) === 0;
      });
    }
    var servicePincodes = (root.dataset.servicePincodes || '').split(',');
    var pinStatus = root.querySelector('[data-ppf-pin-status]');

    // Studios are listed straight away (every active one) and narrowed once a full pincode
    // is typed: studios that list it are preferred. Pick & drop covers the whole service
    // area, so a pincode anywhere in it keeps every active studio on offer even when no
    // studio lists it. A studio that is no longer offered is dropped from the booking, and
    // when exactly one studio is available it is picked automatically.
    function renderStudiosForPincode() {
      var pincode = pincodeCheck.value.trim();
      var complete = pincode.length === 6;
      var active = studios.filter(function (s) { return s.active; });
      var matching = active;
      if (complete) {
        var listed = active.filter(function (s) { return matchesPrefix(pincode, s.pincodes); });
        matching = listed.length ? listed : matchesPrefix(pincode, servicePincodes) ? active : [];
      }
      var container = renderOptions('studio', matching, function (s) { return s.name; }, function (s) { return s.address; }, function (s) {
        state.studioId = s.id; state.studioName = s.name; state.studioAddress = s.address;
        refresh();
      });
      pinStatus.dataset.state = !complete ? '' : matching.length ? 'ok' : 'error';
      pinStatus.textContent = !complete ? '' : matching.length ? copy.studioOk : copy.studioNone;

      var stillListed = matching.some(function (s) { return String(s.id) === String(state.studioId); });
      if (!stillListed) { state.studioId = null; state.studioName = null; state.studioAddress = null; }
      if (!state.studioId && matching.length === 1) {
        state.studioId = matching[0].id; state.studioName = matching[0].name; state.studioAddress = matching[0].address;
      }
      if (state.studioId) {
        var selected = container.querySelector('[data-id="' + state.studioId + '"]');
        if (selected) selected.classList.add('is-selected');
      }
      refresh();
    }

    // --- Shared fields: date/slot, contact details ---
    var dateField = root.querySelector('[data-ppf-field="date"]');
    var slotField = root.querySelector('[data-ppf-field="slot"]');
    var nameField = root.querySelector('[data-ppf-field="name"]');
    var phoneField = root.querySelector('[data-ppf-field="phone"]');
    var emailField = root.querySelector('[data-ppf-field="email"]');
    var notesField = root.querySelector('[data-ppf-field="notes"]');

    // Digits only - registered before the refresh listeners so they see the cleaned value.
    // A pasted "+91 98765 43210" or "098765 43210" keeps just the 10-digit mobile number.
    phoneField.addEventListener('input', function () {
      var digits = phoneField.value.replace(/\D/g, '');
      if (digits.length > 10 && digits.indexOf('91') === 0) digits = digits.slice(2);
      if (digits.length > 10 && digits.charAt(0) === '0') digits = digits.slice(1);
      phoneField.value = digits.slice(0, 10);
    });
    [nameField, phoneField, emailField, notesField].forEach(function (el) {
      el.addEventListener('input', refresh);
      el.addEventListener('change', refresh);
    });

    // Date chips: the next DATE_CHIP_COUNT days, built client-side so a cached page never shows stale dates.
    var dateChips = root.querySelector('[data-ppf-date-chips]');
    if (dateChips) {
      var today = new Date();
      for (var i = 0; i < DATE_CHIP_COUNT; i++) {
        var d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
        var chip = document.createElement('label');
        chip.className = 'ppf-acc__date';
        chip.innerHTML =
          '<input type="radio" name="ppf_date" value="' + isoDate(d) + '">' +
          '<span><small>' + (i === 0 ? 'Today' : i === 1 ? 'Tmrw' : d.toLocaleDateString('en-IN', { weekday: 'short' })) + '</small>' +
          '<strong>' + d.getDate() + '</strong>' +
          '<small>' + d.toLocaleDateString('en-IN', { month: 'short' }) + '</small></span>';
        dateChips.appendChild(chip);
      }
      dateChips.addEventListener('change', function (e) {
        dateField.value = e.target.value;
        refresh();
      });
    }
    root.querySelectorAll('[data-ppf-slot-radio]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        slotField.value = radio.value;
        refresh();
      });
    });

    // --- Validation ---
    // The phone message shows only on Continue, so it doesn't nag while the number is being typed.
    function serviceValid() {
      return !!state.studioId;
    }

    function detailsValid(report) {
      var phoneOk = /^[0-9]{10}$/.test(phoneField.value.trim());
      var phoneError = root.querySelector('[data-ppf-phone-error]');
      if (phoneOk) phoneError.textContent = '';
      else if (report) phoneError.textContent = copy.errorPhone || 'Please enter a valid 10-digit phone number';
      return !!(nameField.value.trim() && phoneOk);
    }

    function allValid() {
      return !!(state.category && state.variantId && dateField.value && slotField.value && serviceValid() && detailsValid());
    }

    function sectionComplete(name) {
      if (name === 'car') return !!state.category;
      if (name === 'package') return !!state.typeId;
      if (name === 'brand') return !!state.packageId;
      if (name === 'service') return serviceValid() && !!dateField.value && !!slotField.value;
      if (name === 'details') return detailsValid();
      return false;
    }

    // First problem in a Continue step, or null when it's complete.
    function stepProblem(name) {
      if (name === 'service') {
        if (!state.studioId) return { message: copy.errorStudio, el: pincodeCheck };
        if (!dateField.value) return { message: copy.errorDate, el: dateChips };
        if (!slotField.value) return { message: copy.errorSlot, el: root.querySelector('.ppf-acc__slots') };
      }
      if (name === 'details') {
        var phoneOk = detailsValid(true);
        if (!nameField.value.trim()) return { message: copy.errorName, el: nameField };
        if (!phoneOk) return { message: copy.errorPhone, el: phoneField };
      }
      return null;
    }

    // --- Accordion ---
    var stepEls = {};
    STEPS.forEach(function (name) { stepEls[name] = root.querySelector('[data-ppf-section="' + name + '"]'); });

    function isComplete(name) {
      if (name === 'service' || name === 'details') return confirmed[name] && sectionComplete(name);
      return sectionComplete(name);
    }

    function isUnlocked(name) {
      return STEPS.slice(0, STEPS.indexOf(name)).every(isComplete);
    }

    function setOpen(el, open) {
      var toggle = el.querySelector('[data-ppf-toggle]');
      var body = el.querySelector('.ppf-acc__body');
      if (!open) el.classList.remove('is-settled');
      el.classList.toggle('is-open', open);
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (body) body.inert = !open;
      if (open && reduceMotion) el.classList.add('is-settled');
    }

    function openStep(name, scroll) {
      STEPS.forEach(function (key) { if (stepEls[key]) setOpen(stepEls[key], key === name); });
      var el = name && stepEls[name];
      if (el && scroll) {
        setTimeout(function () {
          var top = el.getBoundingClientRect().top;
          if (top < 80 || top > window.innerHeight * 0.55) el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        }, reduceMotion ? 0 : 200);
      }
    }

    function openStepName() {
      var open = STEPS.filter(function (key) { return stepEls[key] && stepEls[key].classList.contains('is-open'); });
      return open[0] || null;
    }

    // After a step completes: open the next step that still needs input, or - when everything
    // is done - fold them all and bring the pay button into view.
    function advanceFrom(name) {
      setTimeout(function () {
        var next = null;
        for (var i = STEPS.indexOf(name) + 1; i < STEPS.length; i++) {
          if (!isComplete(STEPS[i])) { next = STEPS[i]; break; }
        }
        openStep(next, true);
        if (!next) revealPay();
      }, ADVANCE_DELAY);
    }

    function revealPay() {
      if (!confirmBtn) return;
      var rect = confirmBtn.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        confirmBtn.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      }
      setTimeout(function () { if (!confirmBtn.disabled) confirmBtn.focus({ preventScroll: true }); }, reduceMotion ? 0 : 450);
    }

    function stepSummary(name) {
      if (name === 'car') return state.carLabel || '';
      if (name === 'package') return state.typeName || '';
      if (name === 'brand') return state.brandName ? state.brandName + ' · ' + formatMoney(state.packagePrice) : '';
      if (name === 'service') {
        var where = (copy.labelPickup || 'Pick & Drop') + (state.studioName ? ' · ' + state.studioName : '');
        var when = dateField.value && slotField.value ? friendlyDate(dateField.value) + ', ' + slotField.value : '';
        return [where, when].filter(Boolean).join(' · ');
      }
      if (name === 'details') return nameField.value.trim() ? nameField.value.trim() + ' · +91 ' + phoneField.value.trim() : '';
      return '';
    }

    function syncAccordion() {
      STEPS.forEach(function (name) {
        var el = stepEls[name];
        if (!el) return;
        var locked = !isUnlocked(name);
        el.classList.toggle('is-locked', locked);
        el.classList.toggle('is-done', !locked && isComplete(name));
        var toggle = el.querySelector('[data-ppf-toggle]');
        if (toggle) toggle.disabled = locked;
        var summary = el.querySelector('[data-ppf-step-summary]');
        if (summary) summary.textContent = stepSummary(name);
      });
      // A step that just became locked (an earlier step was cleared) can't stay open.
      var open = openStepName();
      if (open && !isUnlocked(open)) openStep(STEPS.filter(function (key) { return !isComplete(key); })[0] || null, false);
    }

    STEPS.forEach(function (name) {
      var el = stepEls[name];
      if (!el) return;
      var body = el.querySelector('.ppf-acc__body');
      // Once the open animation ends, let focus rings and card shadows spill past the clipped panel.
      if (body) body.addEventListener('transitionend', function (e) {
        if (e.target === body && e.propertyName === 'grid-template-rows' && el.classList.contains('is-open')) el.classList.add('is-settled');
      });
      var toggle = el.querySelector('[data-ppf-toggle]');
      if (toggle) toggle.addEventListener('click', function () {
        if (toggle.disabled) return;
        openStep(el.classList.contains('is-open') ? null : name, false);
      });
      // Any edit inside a step clears that step's last error message.
      var errorEl = el.querySelector('[data-ppf-step-error]');
      if (errorEl) {
        ['input', 'change'].forEach(function (type) {
          el.addEventListener(type, function () { errorEl.textContent = ''; });
        });
      }
    });

    root.querySelectorAll('[data-ppf-continue]').forEach(function (btn) {
      var el = btn.closest('[data-ppf-section]');
      var name = el && el.getAttribute('data-ppf-section');
      var errorEl = el && el.querySelector('[data-ppf-step-error]');
      btn.addEventListener('click', function () {
        var problem = stepProblem(name);
        if (problem) {
          if (errorEl) errorEl.textContent = problem.message || '';
          if (problem.el) {
            problem.el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
            if (problem.el.focus) problem.el.focus({ preventScroll: true });
          }
          return;
        }
        if (errorEl) errorEl.textContent = '';
        confirmed[name] = true;
        refresh();
        advanceFrom(name);
      });
    });

    // --- Summary sidebar ---
    var summaryCar = root.parentElement.querySelector('[data-ppf-summary-car]');
    var summaryCarLabel = root.parentElement.querySelector('[data-ppf-summary-car-label]');
    var summaryList = root.parentElement.querySelector('[data-ppf-summary]');
    var summaryTotal = root.parentElement.querySelector('[data-ppf-summary-total]');
    var confirmBtn = root.parentElement.querySelector('[data-ppf-confirm]');
    var statusEl = root.parentElement.querySelector('[data-ppf-status]');
    var advanceRow = root.parentElement.querySelector('[data-ppf-advance-row]');
    var advanceLabel = root.parentElement.querySelector('[data-ppf-advance-label]');
    var advanceAmount = root.parentElement.querySelector('[data-ppf-advance-amount]');
    var balanceAmount = root.parentElement.querySelector('[data-ppf-balance-amount]');
    var confirmLabel = confirmBtn;
    var confirmDefaultLabel = confirmBtn ? confirmBtn.textContent.trim() : 'Pay Booking Amount';

    function updateSummary() {
      if (state.carLabel) {
        summaryCar.hidden = false;
        summaryCarLabel.textContent = state.carLabel;
      } else {
        summaryCar.hidden = true;
      }

      var rows = [];
      if (state.typeName) rows.push(['PPF Package', state.typeName]);
      if (state.brandName) rows.push(['PPF Brand', state.brandName]);
      if (state.packageId) rows.push(['Service', SERVICE_LABEL + ' — FREE']);
      if (dateField.value && slotField.value) rows.push(['Slot', friendlyDate(dateField.value) + ', ' + slotField.value]);
      summaryList.innerHTML = rows.map(function (r) { return '<dt>' + r[0] + '</dt><dd>' + r[1] + '</dd>'; }).join('');

      summaryTotal.textContent = state.packagePrice ? formatMoney(state.packagePrice) : '—';

      var advance = advanceFor(state.packagePrice);
      if (advanceRow) {
        advanceRow.hidden = !advance;
        if (advance) {
          if (advanceLabel) advanceLabel.textContent = 'Pay now (' + advancePercent + '% advance)';
          if (advanceAmount) advanceAmount.textContent = formatMoney(advance.amount);
          if (balanceAmount) balanceAmount.textContent = formatMoney(advance.balance);
        }
      }
      if (confirmLabel) {
        confirmLabel.textContent = advance
          ? 'Pay ' + advancePercent + '% Advance · ' + formatMoney(advance.amount)
          : confirmDefaultLabel;
      }
    }

    function refresh() {
      updateSummary();
      syncAccordion();
      confirmBtn.disabled = !allValid();
    }

    // --- Pay / checkout ---
    confirmBtn.addEventListener('click', async function () {
      if (!allValid() || !state.variantId) return;
      confirmBtn.disabled = true;
      confirmBtn.textContent = 'Redirecting to checkout...';
      statusEl.dataset.state = '';
      statusEl.textContent = '';

      var properties = {
        'Booking Type': 'PPF',
        'Car': state.carLabel,
        'Protection Type': state.typeName,
        'PPF Brand': state.brandName,
        'Package': state.packageName,
        'Service': SERVICE_LABEL,
        'Install Date': dateField.value,
        'Install Slot': slotField.value,
        'Customer Name': nameField.value,
        'Customer Phone': phoneField.value
      };
      properties['Studio'] = state.studioName + ' - ' + state.studioAddress;
      if (emailField.value.trim()) properties['Customer Email'] = emailField.value.trim();
      if (notesField.value.trim()) properties['Notes'] = notesField.value.trim();

      // Charging a deposit means the order total no longer equals the package price, so
      // the full breakdown rides along on the order for whoever fulfils the booking.
      var advance = advanceFor(state.packagePrice);
      var previousLabel = confirmBtn.textContent;
      if (advance) {
        properties['Package Price'] = formatMoney(state.packagePrice);
        properties['Advance Paid'] = formatMoney(advance.amount) + ' (' + advancePercent + '%)';
        properties['Balance Due At Installation'] = formatMoney(advance.balance);
      }

      try {
        await window.CarzeloCart.replaceCartAndAdd(
          advance
            ? [{ id: advanceVariantId, quantity: advance.quantity }]
            : [{ id: parseInt(state.variantId, 10), quantity: 1 }],
          properties
        );
        window.location.href = '/checkout';
      } catch (err) {
        statusEl.dataset.state = 'error';
        statusEl.textContent = err.message;
        confirmBtn.disabled = false;
        confirmBtn.textContent = previousLabel;
      }
    });

    renderStudiosForPincode();
    refresh();
    openStep('car', false);
  });
})();
